import { useState, useCallback } from 'react'
import type { UserSettings } from '@fuelfinder/shared'

const SETTINGS_STORAGE_KEY = 'powermap-settings'

const DEFAULT_SETTINGS: UserSettings = {
  speedTier: 'all',
  sortBy: 'nearest',
  radiusMiles: 5,
}

/**
 * Load settings from localStorage with fallback to defaults
 */
function loadSettings(): UserSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...DEFAULT_SETTINGS, ...parsed }
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_SETTINGS
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for managing user settings with localStorage persistence
 */
export function useSettings() {
  const [settings, setSettings] = useState<UserSettings>(loadSettings)

  const updateSettings = useCallback((partial: Partial<UserSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...partial }
      saveSettings(next)
      return next
    })
  }, [])

  return { settings, updateSettings }
}
